import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectItem } from '@/components/ui/select';
import { isDelegatedLocation, type MaybeDelegatedLocation } from '../utils/delegatedLocations';

/**
 * The `<SelectItem>` list for a stock-WRITE location picker.
 *
 * WHY THIS EXISTS, and why it is not just `locations.map(...)`:
 *
 * A delegated ("shadow") location routes its FULFILMENT to a shared 3PL
 * location, so it is not somewhere anything is picked, printed, packed or held
 * back — which is why it is absent from `locations` everywhere an operator is
 * choosing where work happens. But stock is a different question from
 * fulfilment: real `sku_inventory` rows sit at delegated locations today
 * (~200 on production), and an operator has to be able to count them, correct
 * them and transfer them OUT. A picker that cannot name the location holding
 * the stock is a picker that strands it (BLU-879 review).
 *
 * So these four pickers, and only these, read `allLocations`:
 * AddStockModal, IndividualStocktakeModal, AddSkuModal, and TransferCreate's
 * SOURCE select. A transfer DESTINATION, an order's location,
 * the hold-back grid, the global location dropdown and user-management all
 * stay on the filtered list — those all decide where future work happens.
 *
 * Presentation carries the distinction rather than hiding it: delegated
 * entries sort LAST (a list that opens on the normal choices) and carry a
 * quiet "(fulfilled elsewhere)" suffix, so picking one is a deliberate act and
 * never a surprise. Colour is not used; the suffix is the whole signal
 * (DESIGN §16, colour-as-only-information).
 *
 * This component and `excludeDelegatedLocations` are the only two places that
 * make the show/hide decision. A sixth picker that hand-rolls the sort and
 * the suffix is how the two halves drift apart, so extend this instead.
 */

export interface StockWriteLocationOption extends MaybeDelegatedLocation {
  id: string;
  name: string;
}

interface StockWriteLocationItemsProps {
  /** The UNFILTERED list (`allLocations`), delegated rows included. */
  locations: readonly StockWriteLocationOption[];
  /** Drop one id, e.g. a transfer's destination cannot also be its source. */
  excludeId?: string | null;
}

export function StockWriteLocationItems({ locations, excludeId }: StockWriteLocationItemsProps) {
  const { t } = useTranslation('common');

  // Stable partition: direct locations keep their incoming order, delegated
  // ones keep theirs and follow. Not a comparator — a sort would invite a
  // second ordering rule to creep in beside the delegation one.
  const ordered = useMemo(() => {
    const direct: StockWriteLocationOption[] = [];
    const delegated: StockWriteLocationOption[] = [];
    for (const location of locations) {
      if (excludeId && location.id === excludeId) continue;
      (isDelegatedLocation(location) ? delegated : direct).push(location);
    }
    return [...direct, ...delegated];
  }, [locations, excludeId]);

  return (
    <>
      {ordered.map((location) => (
        <SelectItem key={location.id} value={location.id}>
          {location.name}
          {isDelegatedLocation(location) && (
            <span className="ml-1.5 text-xs text-muted-foreground">
              {t('location.fulfilledElsewhere')}
            </span>
          )}
        </SelectItem>
      ))}
    </>
  );
}
